/**
 * Hook for managing chat file attachments with validation.
 */

import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import type { ChatAttachment } from '@/components/chat/chat-input-attachment.interface'
import { validateFileList } from '@/components/chat/chat-input-attachment-validation.helper'

export interface UseChatAttachmentsReturn {
  attachmentList: ChatAttachment[]
  handleRemoveAttachment: (id: string) => void
  handleFilesSelected: (fileList: File[]) => void
  clearAttachmentList: () => void
  restoreAttachmentList: (list: ChatAttachment[]) => void
}

const createImageAttachment = (file: File, id: string): ChatAttachment => ({
  id,
  type: 'image' as const,
  name: file.name,
  size: file.size,
  mimeType: file.type,
  source: 'computer' as const,
  previewUrl: URL.createObjectURL(file),
  file,
})

const createFileAttachment = (file: File, id: string): ChatAttachment => ({
  id,
  type: 'file' as const,
  name: file.name,
  size: file.size,
  mimeType: file.type,
  source: 'computer' as const,
  file,
})

const createAttachmentFromFile = (file: File, index: number): ChatAttachment => {
  const isImage = file.type.startsWith('image/')
  const id = `${isImage ? 'img' : 'file'}-${Date.now()}-${index}`
  if (isImage) return createImageAttachment(file, id)
  return createFileAttachment(file, id)
}

export const useChatAttachments = (): UseChatAttachmentsReturn => {
  const [attachmentList, setAttachmentList] = useState<ChatAttachment[]>([])

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachmentList((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const handleFilesSelected = useCallback(
    (fileList: File[]) => {
      const { validFileList, errorMessageList } = validateFileList(fileList, attachmentList.length)

      for (const errorMessage of errorMessageList) {
        toast.error(errorMessage)
      }

      if (validFileList.length === 0) return

      const newList = validFileList.map(createAttachmentFromFile)
      setAttachmentList((prev) => [...prev, ...newList])
    },
    [attachmentList.length]
  )

  const clearAttachmentList = useCallback(() => {
    setAttachmentList([])
  }, [])

  const restoreAttachmentList = useCallback((list: ChatAttachment[]) => {
    setAttachmentList(list)
  }, [])

  return {
    attachmentList,
    handleRemoveAttachment,
    handleFilesSelected,
    clearAttachmentList,
    restoreAttachmentList,
  }
}
